/**
 * build-pc-i18n.js — CompuMe PC Builder Phase 2 (v2.3)
 * A) Arabic/English UI toggle
 * B) Share My Build — v2.3: relaxed gate, full wizard state in payload
 * C) WhatsApp
 *
 * SECURITY CONTRACT (unchanged):
 *  - price NOT encoded — always fetched live
 *  - title = skeleton display fallback only
 *  - No customer, email, flash, session, token, ip, or AI data ever encoded
 *
 * Share gate change (v2.3):
 *  - Old: required ≥1 component in BP.build
 *  - New: any meaningful selection (useCase OR monitor_target OR tier OR any build slot)
 *  - Wizard-only share opens the builder at the shared starting point
 *  - Parts share opens exact build (live-fetched, with unavailability warnings)
 *
 * TODO Phase 2.1:
 *  - Full RTL/i18n re-render for dynamic Stage 2/3/4 content.
 */
'use strict';

const BP_WA_NUMBER = '962798000180';
const BP_WA_URL    = 'https://wa.me/' + BP_WA_NUMBER;

/* ═══════════════════════════════════════════════════════════
   A) LOCALE / i18n
═══════════════════════════════════════════════════════════ */
const BP_I18N = {
  en: {
    locale:'en', dir:'ltr',
    headline:'Build Your<br>Gaming PC',
    subhead:'Answer 2 questions \u2014 we build your perfect rig.',
    discover:'Discover', blueprint:'Blueprint', customize:'Customize', review:'Review',
    useCase:'1. What will you use it for?',
    monitorQ:'2. What monitor target are you building for?',
    monitorHelper:'Choose your monitor level so we can match the right GPU and CPU.',
    budget:'3. Budget (JOD)',
    showBuild:'Show Me a Build \u2192',
    yourBuild:'Your Build', components:'/8 components',
    reset:'Reset', continueBtn:'Continue \u2192',
    changeTier:'\u2190 Change Tier', changeUseCase:'\u2190 Change Use Case', editParts:'\u2190 Edit Parts',
    reviewBuild:'Review Your Build', reviewSubhead:'Fine-tune add-ons. Then add to cart.',
    pickParts:'Pick Your Parts', pickSubhead:'Select one from each category. Compatibility checked live.',
    serviceAddons:'Service Add-ons', addToCart:'Add to Cart & Checkout \u2192',
    notSelected:'Not selected yet', optional:'Optional',
    subtotal:'Subtotal (components + services)', estTotal:'Estimated Total',
    discountLine:'\u2605 PC Builder Discount (3% off total)', flashBonus:'\u26a1 Flash Bonus (2%)',
    discountNote:'> [DISCOUNT_ACTIVE] 3% PC Builder discount applied automatically at checkout \u2014 no code needed.',
    shareBtn:'Share My Build', copyLink:'Copy Link', linkCopied:'Link Copied!',
    waShareMsg:'Check out my CompuMe gaming build!',
    shareEmpty:'Make a selection first to share your build.',
    aiPlaceholder:'> Type your build request...', aiLabel:'AI message', openAI:'Open AI',
    yourBlueprint:'Your Blueprint', blueprintSub:'Select a tier \u2014 then customise every part.',
    categories:{cpu:'CPU',motherboard:'Motherboard',ram:'RAM',gpu:'GPU',psu:'Power Supply',
      storage:'Storage',case:'Case',cooler:'CPU Cooler',fans:'Case Fans',thermalpaste:'Thermal Paste',
      os:'Operating System',build_fee:'Build & Assembly',warranty:'Warranty'},
    inStock:'In Stock', outOfStock:'Out of Stock', selected:'\u2713 selected',
    addingToCart:'Adding to cart...', addToCartFallback:'Add to Cart \u2192',
    noProducts:'No products found.', notConfigured:'Not configured yet.',
    noComponents:'No components yet.', pickPartsLink:'Pick your parts \u2192',
    loadingTiers:'Loading tiers...', errorTiers:'> ERROR: Could not load tiers.',
    restoreOOS:'Previously selected item is no longer available.',
    restoreVariantChanged:'Note: variant changed \u2014 please verify your selection.',
    monitor: {
      esports_entry:{ title:'Esports Entry', desc:'Valorant \u00b7 CS2 \u00b7 Fortnite', for:'Competitive FPS' },
      balanced:     { title:'Balanced Gaming', desc:'Best balance for most gamers', for:'Most Players' },
      qhd_240:      { title:'High Refresh QHD', desc:'Fast performance with sharper image quality', for:'Fast + Sharp' },
      '4k':         { title:'4K Cinematic', desc:'AAA \u00b7 Story Games \u00b7 Console + PC', for:'AAA / Console' },
      oled:         { title:'Premium OLED / UltraWide', desc:'Premium gaming and content experience', for:'Premium Setup' },
    },
  },
  ar: {
    locale:'ar', dir:'rtl',
    headline:'\u0627\u0635\u0646\u0639 \u062c\u0647\u0627\u0632 \u0627\u0644\u0623\u0644\u0639\u0627\u0628 \u0627\u0644\u062e\u0627\u0635 \u0628\u0643',
    subhead:'\u0623\u062c\u0628 \u0639\u0646 \u0633\u0624\u0627\u0644\u064a\u0646 \u2014 \u0646\u0628\u0646\u064a \u062c\u0647\u0627\u0632\u0643 \u0627\u0644\u0645\u062b\u0627\u0644\u064a.',
    discover:'\u0627\u0643\u062a\u0634\u0627\u0641', blueprint:'\u0627\u0644\u062e\u0637\u0629',
    customize:'\u062a\u062e\u0635\u064a\u0635', review:'\u0645\u0631\u0627\u062c\u0639\u0629',
    useCase:'\u0661. \u0645\u0627\u0630\u0627 \u0633\u062a\u0633\u062a\u062e\u062f\u0645\u0647\u061f',
    monitorQ:'\u0662. \u0645\u0627 \u0646\u0648\u0639 \u0627\u0644\u0634\u0627\u0634\u0629 \u0627\u0644\u0645\u0633\u062a\u0647\u062f\u0641\u0629\u061f',
    monitorHelper:'\u0627\u062e\u062a\u0631 \u0645\u0633\u062a\u0648\u0649 \u0627\u0644\u0634\u0627\u0634\u0629 \u062d\u062a\u0649 \u0646\u0642\u062a\u0631\u062d \u0643\u0631\u062a \u0634\u0627\u0634\u0629 \u0648\u0645\u0639\u0627\u0644\u062c \u0645\u0646\u0627\u0633\u0628\u064a\u0646.',
    budget:'\u0663. \u0627\u0644\u0645\u064a\u0632\u0627\u0646\u064a\u0629 (JOD)',
    showBuild:'\u0627\u0628\u062f\u0623 \u0627\u0644\u062a\u0631\u0643\u064a\u0628',
    yourBuild:'\u062a\u0643\u0648\u064a\u0646\u0643', components:'/8 \u0645\u0643\u0648\u0646\u0627\u062a',
    reset:'\u0625\u0639\u0627\u062f\u0629 \u062a\u0639\u064a\u064a\u0646', continueBtn:'\u0645\u062a\u0627\u0628\u0639\u0629 \u2190',
    changeTier:'\u062a\u063a\u064a\u064a\u0631 \u0627\u0644\u0645\u0633\u062a\u0648\u0649 \u2190',
    changeUseCase:'\u062a\u063a\u064a\u064a\u0631 \u0627\u0644\u0627\u0633\u062a\u062e\u062f\u0627\u0645 \u2190',
    editParts:'\u062a\u0639\u062f\u064a\u0644 \u0627\u0644\u0642\u0637\u0639 \u2190',
    reviewBuild:'\u0645\u0631\u0627\u062c\u0639\u0629 \u062a\u0643\u0648\u064a\u0646\u0643',
    reviewSubhead:'\u0636\u0628\u0637 \u0627\u0644\u0625\u0636\u0627\u0641\u0627\u062a. \u062b\u0645 \u0623\u0636\u0641 \u0625\u0644\u0649 \u0627\u0644\u0633\u0644\u0629.',
    pickParts:'\u0627\u062e\u062a\u0631 \u0642\u0637\u0639\u0643',
    pickSubhead:'\u0627\u062e\u062a\u0631 \u0648\u0627\u062d\u062f\u064b\u0627 \u0645\u0646 \u0643\u0644 \u0641\u0626\u0629. \u0627\u0644\u062a\u0648\u0627\u0641\u0642 \u064a\u064f\u0641\u062d\u0635 \u0641\u0648\u0631\u064b\u0627.',
    serviceAddons:'\u0627\u0644\u062e\u062f\u0645\u0627\u062a \u0627\u0644\u0625\u0636\u0627\u0641\u064a\u0629', addToCart:'\u0623\u0636\u0641 \u0625\u0644\u0649 \u0627\u0644\u0633\u0644\u0629',
    notSelected:'\u0644\u0645 \u064a\u062a\u0645 \u0627\u0644\u0627\u062e\u062a\u064a\u0627\u0631 \u0628\u0639\u062f', optional:'\u0627\u062e\u062a\u064a\u0627\u0631\u064a',
    subtotal:'\u0627\u0644\u0645\u062c\u0645\u0648\u0639 \u0627\u0644\u062c\u0632\u0626\u064a', estTotal:'\u0627\u0644\u0645\u062c\u0645\u0648\u0639',
    discountLine:'\u2605 \u062e\u0635\u0645 3% \u0639\u0644\u0649 \u0627\u0644\u0642\u0637\u0639', flashBonus:'\u26a1 \u0645\u0643\u0627\u0641\u0623\u0629 \u0625\u0636\u0627\u0641\u064a\u0629 (2%)',
    discountNote:'> [DISCOUNT_ACTIVE] \u062e\u0635\u0645 3% \u064a\u064f\u0637\u0628\u0642 \u062a\u0644\u0642\u0627\u0626\u064a\u064b\u0627 \u0639\u0646\u062f \u0627\u0644\u062f\u0641\u0639 \u2014 \u062f\u0648\u0646 \u0643\u0648\u062f.',
    shareBtn:'\u0634\u0627\u0631\u0643 \u062a\u0643\u0648\u064a\u0646\u064a', copyLink:'\u0646\u0633\u062e \u0627\u0644\u0631\u0627\u0628\u0637', linkCopied:'\u062a\u0645 \u0646\u0633\u062e \u0627\u0644\u0631\u0627\u0628\u0637!',
    waShareMsg:'\u0634\u0627\u0647\u062f \u062a\u0643\u0648\u064a\u0646 \u062c\u0647\u0627\u0632 \u0627\u0644\u0623\u0644\u0639\u0627\u0628 \u0627\u0644\u062e\u0627\u0635 \u0628\u064a \u0645\u0646 CompuMe!',
    shareEmpty:'\u0627\u062e\u062a\u0631 \u0634\u064a\u0626\u064b\u0627 \u0623\u0648\u0644\u0627\u064b \u0644\u0645\u0634\u0627\u0631\u0643\u0629 \u062a\u0643\u0648\u064a\u0646\u0643.',
    aiPlaceholder:'> \u0627\u0643\u062a\u0628 \u0637\u0644\u0628\u0643...', aiLabel:'\u0631\u0633\u0627\u0644\u0629 \u0644\u0644\u0630\u0643\u0627\u0621 \u0627\u0644\u0627\u0635\u0637\u0646\u0627\u0639\u064a', openAI:'\u0641\u062a\u062d \u0627\u0644\u0630\u0643\u0627\u0621 \u0627\u0644\u0627\u0635\u0637\u0646\u0627\u0639\u064a',
    yourBlueprint:'\u062e\u0637\u062a\u0643', blueprintSub:'\u0627\u062e\u062a\u0631 \u0645\u0633\u062a\u0648\u0649 \u2014 \u062b\u0645 \u062e\u0635\u0635 \u0643\u0644 \u0642\u0637\u0639\u0629.',
    categories:{cpu:'\u0627\u0644\u0645\u0639\u0627\u0644\u062c',motherboard:'\u0627\u0644\u0644\u0648\u062d\u0629 \u0627\u0644\u0623\u0645',ram:'\u0627\u0644\u0630\u0627\u0643\u0631\u0629',gpu:'\u0643\u0631\u062a \u0627\u0644\u0634\u0627\u0634\u0629',psu:'\u0645\u0632\u0648\u062f \u0627\u0644\u0637\u0627\u0642\u0629',storage:'\u0627\u0644\u062a\u062e\u0632\u064a\u0646',case:'\u0627\u0644\u0635\u0646\u062f\u0648\u0642',cooler:'\u0627\u0644\u0645\u0628\u0631\u062f',fans:'\u0627\u0644\u0645\u0631\u0627\u0648\u062d',thermalpaste:'\u0627\u0644\u0645\u0639\u062c\u0648\u0646 \u0627\u0644\u062d\u0631\u0627\u0631\u064a',os:'\u0646\u0638\u0627\u0645 \u0627\u0644\u062a\u0634\u063a\u064a\u0644',build_fee:'\u0627\u0644\u062a\u062c\u0645\u064a\u0639 \u0648\u0627\u0644\u062a\u0631\u0643\u064a\u0628',warranty:'\u0627\u0644\u0636\u0645\u0627\u0646'},
    inStock:'\u0645\u062a\u0648\u0641\u0631', outOfStock:'\u063a\u064a\u0631 \u0645\u062a\u0648\u0641\u0631', selected:'\u2713 \u062a\u0645 \u0627\u0644\u0627\u062e\u062a\u064a\u0627\u0631',
    addingToCart:'\u062c\u0627\u0631\u064a \u0627\u0644\u0625\u0636\u0627\u0641\u0629...', addToCartFallback:'\u0623\u0636\u0641 \u0625\u0644\u0649 \u0627\u0644\u0633\u0644\u0629',
    noProducts:'\u0644\u0627 \u062a\u0648\u062c\u062f \u0645\u0646\u062a\u062c\u0627\u062a.', notConfigured:'\u0644\u0645 \u064a\u062a\u0645 \u0627\u0644\u062a\u0643\u0648\u064a\u0646 \u0628\u0639\u062f.',
    noComponents:'\u0644\u0627 \u062a\u0648\u062c\u062f \u0645\u0643\u0648\u0646\u0627\u062a \u0628\u0639\u062f.', pickPartsLink:'\u0627\u062e\u062a\u0631 \u0642\u0637\u0639\u0643 \u2192',
    loadingTiers:'\u062c\u0627\u0631\u064a \u0627\u0644\u062a\u062d\u0645\u064a\u0644...', errorTiers:'> \u062e\u0637\u0623: \u062a\u0639\u0630\u0631 \u062a\u062d\u0645\u064a\u0644 \u0627\u0644\u0628\u064a\u0627\u0646\u0627\u062a.',
    restoreOOS:'\u0627\u0644\u0639\u0646\u0635\u0631 \u0627\u0644\u0645\u062d\u062f\u062f \u0645\u0633\u0628\u0642\u064b\u0627 \u0644\u0645 \u064a\u0639\u062f \u0645\u062a\u0627\u062d\u064b\u0627.',
    restoreVariantChanged:'\u062a\u0646\u0628\u064a\u0647: \u062a\u063a\u064a\u0631 \u0627\u0644\u0625\u0635\u062f\u0627\u0631 \u2014 \u064a\u0631\u062c\u0649 \u0645\u0631\u0627\u062c\u0639\u0629 \u0627\u062e\u062a\u064a\u0627\u0631\u0643.',
    monitor: {
      esports_entry:{ title:'\u0623\u0644\u0639\u0627\u0628 \u062a\u0646\u0627\u0641\u0633\u064a\u0629 Entry', desc:'Valorant \u00b7 CS2 \u00b7 Fortnite', for:'\u0623\u0644\u0639\u0627\u0628 FPS \u0627\u0644\u062a\u0646\u0627\u0641\u0633\u064a\u0629' },
      balanced:     { title:'\u062a\u062c\u0631\u0628\u0629 \u0645\u062a\u0648\u0627\u0632\u0646\u0629', desc:'\u0623\u0641\u0636\u0644 \u062a\u0648\u0627\u0632\u0646 \u0644\u0645\u0639\u0638\u0645 \u0627\u0644\u0644\u0627\u0639\u0628\u064a\u0646', for:'\u0645\u0639\u0638\u0645 \u0627\u0644\u0644\u0627\u0639\u0628\u064a\u0646' },
      qhd_240:      { title:'QHD \u0633\u0631\u064a\u0639', desc:'\u0644\u0623\u062f\u0627\u0621 \u0633\u0631\u064a\u0639 \u0648\u0635\u0648\u0631\u0629 \u0623\u0648\u0636\u062d', for:'\u0633\u0631\u064a\u0639 + \u0648\u0627\u0636\u062d' },
      '4k':         { title:'\u0633\u064a\u0646\u0645\u0627\u0626\u064a 4K', desc:'AAA \u00b7 Story Games \u00b7 Console + PC', for:'AAA / Console' },
      oled:         { title:'OLED / UltraWide \u0641\u0627\u062e\u0631', desc:'\u062a\u062c\u0631\u0628\u0629 \u0641\u0627\u062e\u0631\u0629 \u0644\u0644\u0623\u0644\u0639\u0627\u0628 \u0648\u0627\u0644\u0645\u062d\u062a\u0648\u0649', for:'\u0625\u0639\u062f\u0627\u062f \u0641\u0627\u062e\u0631' },
    },
  }
};

let BP_LOCALE = localStorage.getItem('compume_locale') || 'en';
if (!BP_I18N[BP_LOCALE]) BP_LOCALE = 'en';
window.BP_LOCALE = BP_LOCALE;
window.BP_T = function() { return BP_I18N[BP_LOCALE]; };
function bpT(key) {
  const t = BP_I18N[BP_LOCALE];
  return key.split('.').reduce((o, k) => o?.[k], t) || key;
}
window.bpT = bpT;

function bpApplyLocale(locale) {
  if (!BP_I18N[locale]) return;
  BP_LOCALE = locale;
  window.BP_LOCALE = locale;
  localStorage.setItem('compume_locale', locale);
  const root = document.getElementById('bp-root');
  if (root) { root.setAttribute('dir', BP_I18N[locale].dir); root.setAttribute('lang', locale); }
  const toggle = document.getElementById('bp-locale-toggle');
  if (toggle) toggle.textContent = locale === 'en' ? '\u0627\u0644\u0639\u0631\u0628\u064a\u0629' : 'EN';
  const barLabel = document.querySelector('.bp-summary-bar__label');
  if (barLabel) barLabel.textContent = bpT('yourBuild');
  const resetBtn = document.querySelector('.bp-summary-bar__actions .bp-btn--ghost:not(#bp-share-btn)');
  if (resetBtn) resetBtn.textContent = bpT('reset');
  const contBtn = document.getElementById('bp-continue-btn');
  if (contBtn) contBtn.textContent = bpT('continueBtn');
  ['discover','blueprint','customize','review'].forEach((k, i) => {
    const el = document.querySelector(`#bp-nav-${i+1} span`);
    if (el) el.textContent = bpT(k);
  });
  const aiInput = document.getElementById('bp-ai-input');
  if (aiInput) aiInput.placeholder = bpT('aiPlaceholder');
  const aiFab = document.getElementById('bp-ai-fab');
  if (aiFab) aiFab.setAttribute('aria-label', bpT('openAI'));
  const h1 = document.querySelector('#bp-stage-1 .bp-headline');
  if (h1) h1.innerHTML = bpT('headline');
  const sub1 = document.querySelector('#bp-stage-1 .bp-subhead');
  if (sub1) sub1.textContent = bpT('subhead');
  const labels = document.querySelectorAll('#bp-stage-1 .bp-section-label');
  if (labels[0]) labels[0].textContent = bpT('useCase');
  if (labels[1]) labels[1].textContent = bpT('budget');
  const monLabel = document.getElementById('bp-monitor-label');
  if (monLabel) monLabel.textContent = bpT('monitorQ');
  const monHelper = document.getElementById('bp-monitor-helper');
  if (monHelper) monHelper.textContent = bpT('monitorHelper');
  document.querySelectorAll('#bp-monitor-grid [data-i18n]').forEach(el => {
    const key = el.dataset.i18n;
    const text = bpT(key);
    if (text && text !== key) el.textContent = text;
  });
  const showBtn = document.getElementById('bp-show-build-btn');
  if (showBtn) showBtn.textContent = bpT('showBuild');
  const shareBtn = document.getElementById('bp-share-btn');
  if (shareBtn) shareBtn.textContent = '\ud83d\udd17 ' + bpT('shareBtn');
}
window.bpApplyLocale = bpApplyLocale;
function bpToggleLocale() { bpApplyLocale(BP_LOCALE === 'en' ? 'ar' : 'en'); }
window.bpToggleLocale = bpToggleLocale;

/* ═══════════════════════════════════════════════════════════
   B) SHARE — v2.3
═══════════════════════════════════════════════════════════ */
const BP_SHARE_FORBIDDEN = new Set([
  'customer_email','customer_id','flash_code','flash_eligible_at',
  'session_token','ai_message_history','ai_message_count',
  'email','phone','customer','ip','fingerprint',
  'hydrated_products','computed','shpat','token','admin_token',
  'storefront_token','bp_storefront_token'
]);
function bpSanitizeForShare(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(bpSanitizeForShare);
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (BP_SHARE_FORBIDDEN.has(k.toLowerCase())) continue;
    if (v instanceof ArrayBuffer || ArrayBuffer.isView(v)) continue;
    out[k] = bpSanitizeForShare(v);
  }
  return out;
}

function bpBuildSharePayload() {
  const BP = window.BP || {};
  const items = {};
  for (const [k, v] of Object.entries(BP.build || {})) {
    if (!v || !v.variantId) continue;
    items[k] = bpSanitizeForShare({ variantId:v.variantId, id:v.id||null, handle:v.handle||null, title:v.title||null });
  }
  return {
    v: 3,
    useCase:        BP.useCase        || null,
    resolution:     BP.resolution     || null,
    monitor_target: BP.monitor_target || null,
    refresh_target: BP.refresh_target ?? null,
    monitor_tier:   BP.monitor_tier   || null,
    budget:         BP.budget         || null,
    tier_id:        BP.tier?.id       || null,
    items,
  };
}

function bpEncodeShare(payload) {
  try { const bytes=new TextEncoder().encode(JSON.stringify(payload)); let b=''; bytes.forEach(x=>{b+=String.fromCharCode(x);}); return btoa(b); }
  catch(e){console.warn('[BP Share] encode error:',e);return null;}
}
function bpDecodeShare(encoded) {
  try { if(!encoded||encoded.length>8000)return null; const bytes=Uint8Array.from(atob(encoded),c=>c.charCodeAt(0)); return JSON.parse(new TextDecoder().decode(bytes)); }
  catch(e){console.warn('[BP Share] decode error:',e);return null;}
}

function bpGenerateShareURL() {
  const payload = bpBuildSharePayload();
  const hasMeaningful = payload.useCase || payload.monitor_target || payload.tier_id || Object.keys(payload.items).length > 0;
  if (!hasMeaningful) return null;
  const encoded = bpEncodeShare(payload); if (!encoded) return null;
  const p = new URLSearchParams(); p.set('b', encoded);
  return window.location.origin + window.location.pathname + '#' + p.toString();
}
window.bpGenerateShareURL = bpGenerateShareURL;

function bpShowRestoreWarning(catKey, reason) {
  const alerts = document.getElementById('bp-compat-alerts'); if (!alerts) return;
  const msgs = { oos:bpT('restoreOOS'), deleted:bpT('restoreOOS'), variant_changed:bpT('restoreVariantChanged') };
  const text = msgs[reason] || bpT('restoreOOS');
  const catLabel = bpT('categories.'+catKey) || catKey.toUpperCase();
  const existing = document.getElementById('bp-restore-warn-'+catKey); if (existing) existing.remove();
  const banner = document.createElement('div');
  banner.id = 'bp-restore-warn-'+catKey; banner.className = 'bp-compat-banner warn';
  banner.textContent = `> [${catLabel.toUpperCase()}] ${text}`;
  alerts.appendChild(banner);
  setTimeout(()=>banner.remove(), 12000);
}

async function bpRehydrateSharedBuild(sharedItems) {
  if (!window.BP_API || !window.BP_STOREFRONT_TOKEN) return;
  const build = window.BP?.build; if (!build) return;
  for (const [catKey, item] of Object.entries(sharedItems)) {
    if (!item.handle || !(catKey in build)) continue;
    try {
      const q = `{productByHandle(handle:"${item.handle}"){id title priceRange{minVariantPrice{amount}} featuredImage{url altText} variants(first:10){edges{node{id availableForSale}}}}}`;
      const r = await fetch(window.BP_API, {method:'POST',headers:{'Content-Type':'application/json','X-Shopify-Storefront-Access-Token':window.BP_STOREFRONT_TOKEN},body:JSON.stringify({query:q})});
      const data = await r.json(); const p = data?.data?.productByHandle;
      if (!p) { build[catKey]=null; bpShowRestoreWarning(catKey,'deleted'); continue; }
      const urlVariantId = String(item.variantId);
      const matchedVariant = p.variants.edges.find(e=>String(typeof gidToNum==='function'?gidToNum(e.node.id):e.node.id)===urlVariantId);
      const livePrice = parseFloat(p.priceRange?.minVariantPrice?.amount||0);
      if (!matchedVariant) {
        const firstAvail = p.variants.edges.find(e=>e.node.availableForSale);
        if (!firstAvail) { build[catKey]=null; bpShowRestoreWarning(catKey,'deleted'); }
        else { build[catKey]={id:p.id,handle:item.handle,title:p.title,price:livePrice,variantId:typeof gidToNum==='function'?gidToNum(firstAvail.node.id):firstAvail.node.id,image:p.featuredImage?.url||'',_shared:true,_variantChanged:true}; bpShowRestoreWarning(catKey,'variant_changed'); }
      } else if (!matchedVariant.node.availableForSale) {
        build[catKey]=null; bpShowRestoreWarning(catKey,'oos');
      } else {
        build[catKey]=Object.assign({},build[catKey]||{},{id:p.id,handle:item.handle,title:p.title,price:livePrice,variantId:typeof gidToNum==='function'?gidToNum(matchedVariant.node.id):matchedVariant.node.id,image:p.featuredImage?.url||'',_shared:true});
      }
    } catch(e) { console.warn('[BP Share] rehydrate error:',catKey,e); build[catKey]=null; bpShowRestoreWarning(catKey,'deleted'); }
  }
  if (typeof bpRefreshBar==='function') bpRefreshBar();
  if (typeof bpRunCompatCheck==='function') bpRunCompatCheck();
}
window.bpRehydrateSharedBuild = bpRehydrateSharedBuild;

async function bpRestoreFromShareHash() {
  const hash = window.location.hash.slice(1);
  const params = new URLSearchParams(hash);
  const encoded = params.get('b'); if (!encoded) return false;
  const payload = bpDecodeShare(encoded); if (!payload) return false;
  console.log('[BP Share] Restoring v'+(payload.v||1));
  const BP = window.BP; if (!BP) return false;
  if (payload.useCase)        BP.useCase        = payload.useCase;
  if (payload.resolution)     BP.resolution     = payload.resolution;
  if (payload.monitor_target) BP.monitor_target = payload.monitor_target;
  if (payload.refresh_target != null) BP.refresh_target = payload.refresh_target;
  if (payload.monitor_tier)   BP.monitor_tier   = payload.monitor_tier;
  if (payload.budget)         BP.budget         = payload.budget;
  if (payload.tier_id) BP._shared_tier_id = payload.tier_id;
  if (payload.budget) {
    const slider = document.getElementById('bp-budget-slider');
    if (slider && typeof bpUpdateBudget==='function') { slider.value=payload.budget; bpUpdateBudget(slider); }
  }
  if (payload.useCase) { const btn=document.querySelector(`[data-use-case="${payload.useCase}"]`); if(btn){btn.classList.add('bp-selected');btn.setAttribute('aria-pressed','true');} }
  if (payload.monitor_target) { const mc=document.querySelector(`[data-monitor-target="${payload.monitor_target}"]`); if(mc){mc.classList.add('bp-selected');mc.setAttribute('aria-pressed','true');} }
  const sharedItems = {};
  const itemsMap = payload.items || {};
  for (const [key, item] of Object.entries(itemsMap)) {
    if (BP.build && key in BP.build) {
      sharedItems[key] = item;
      BP.build[key] = {id:item.id||null,handle:item.handle||null,title:item.title||'',price:0,variantId:item.variantId||null,_shared:true,_pending:true};
    }
  }
  const hasComponents = Object.keys(sharedItems).length > 0;
  if (typeof bpCheckWizardReady==='function') bpCheckWizardReady();
  if (typeof bpRefreshBar==='function') bpRefreshBar();
  if (hasComponents) await bpRehydrateSharedBuild(sharedItems);
  return { hasComponents, hasTier: !!payload.tier_id, hasWizard: !!(payload.useCase||payload.monitor_target) };
}
window.bpRestoreFromShareHash = bpRestoreFromShareHash;

function bpShowSharePanel() {
  const BP = window.BP || {};
  const hasMeaningful = BP.useCase || BP.monitor_target || BP.tier || Object.values(BP.build||{}).some(v=>v);
  if (!hasMeaningful) { alert(bpT('shareEmpty')); return; }
  const url = bpGenerateShareURL(); if (!url) return;
  const t = BP_I18N[BP_LOCALE];
  const waMsg = encodeURIComponent(t.waShareMsg+' '+url);
  const waURL = BP_WA_URL+'?text='+waMsg;
  const old = document.getElementById('bp-share-panel'); if (old) old.remove();
  const panel = document.createElement('div');
  panel.id='bp-share-panel'; panel.className='bp-share-panel';
  panel.setAttribute('role','dialog'); panel.setAttribute('aria-modal','true'); panel.setAttribute('aria-label',t.shareBtn);
  panel.innerHTML=`<div class="bp-share-panel__inner"><div class="bp-share-panel__title">\ud83d\udd17 ${t.shareBtn}</div><input class="bp-share-panel__url" id="bp-share-url-input" value="${url.replace(/"/g,'&quot;')}" readonly aria-label="Share URL"><div class="bp-share-panel__actions"><button class="bp-btn bp-btn--ghost" id="bp-share-copy" type="button">${t.copyLink}</button><a class="bp-btn bp-btn--ghost bp-share-wa" href="${waURL}" target="_blank" rel="noopener noreferrer"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style="vertical-align:middle;margin-inline-end:6px"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>WhatsApp</a><button class="bp-btn bp-btn--ghost" id="bp-share-close" type="button" aria-label="Close">&#10005;</button></div></div>`;
  document.body.appendChild(panel);
  if (navigator.share) {
    const nb=document.createElement('button'); nb.className='bp-btn bp-btn--ghost'; nb.textContent='\ud83d\udcf1 Share';
    nb.onclick=()=>navigator.share({title:'My CompuMe PC Build',url}).catch(()=>{});
    panel.querySelector('.bp-share-panel__actions').insertBefore(nb,panel.querySelector('#bp-share-copy'));
  }
  document.getElementById('bp-share-copy').onclick=async()=>{
    try{await navigator.clipboard.writeText(url);}catch{const i=document.getElementById('bp-share-url-input');i.select();document.execCommand('copy');}
    const b=document.getElementById('bp-share-copy');
    if(b){b.textContent=bpT('linkCopied');setTimeout(()=>b.textContent=bpT('copyLink'),2000);}
  };
  document.getElementById('bp-share-close').onclick=()=>panel.remove();
  panel.addEventListener('click',e=>{if(e.target===panel)panel.remove();});
  panel.addEventListener('keydown',e=>{
    if(e.key!=='Tab')return;
    const f=panel.querySelectorAll('button,a[href],input');
    if(!f.length)return;
    if(e.shiftKey&&document.activeElement===f[0]){e.preventDefault();f[f.length-1].focus();}
    else if(!e.shiftKey&&document.activeElement===f[f.length-1]){e.preventDefault();f[0].focus();}
  });
  document.getElementById('bp-share-close').focus();
}
window.bpShowSharePanel = bpShowSharePanel;

(function(){
  if(document.getElementById('bp-share-css'))return;
  const s=document.createElement('style');s.id='bp-share-css';
  s.textContent=`.bp-share-panel{position:fixed;inset:0;z-index:500;background:rgba(0,0,0,0.75);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;padding:16px;}.bp-share-panel__inner{background:#0c0c12;border:1px solid rgba(0,212,255,0.3);border-radius:12px;padding:24px;max-width:480px;width:100%;box-shadow:0 0 40px rgba(0,212,255,0.15);}.bp-share-panel__title{font-family:var(--bp-font-display);font-weight:900;font-size:20px;text-transform:uppercase;letter-spacing:0.08em;color:var(--bp-cyan);margin-bottom:16px;}.bp-share-panel__url{width:100%;background:#070710;border:1px solid rgba(0,212,255,0.2);color:var(--bp-muted);font-family:var(--bp-font-mono);font-size:12px;padding:10px 12px;border-radius:6px;margin-bottom:14px;outline:none;word-break:break-all;}.bp-share-panel__actions{display:flex;gap:10px;flex-wrap:wrap;}.bp-share-wa{color:#25D366;border-color:rgba(37,211,102,0.3);text-decoration:none;display:inline-flex;align-items:center;}.bp-share-wa:hover{border-color:#25D366;background:rgba(37,211,102,0.06);}#bp-locale-toggle{position:absolute;top:14px;right:16px;z-index:20;background:transparent;border:1px solid rgba(0,212,255,0.3);color:var(--bp-cyan);font-family:var(--bp-font-display);font-weight:700;font-size:13px;text-transform:uppercase;letter-spacing:0.08em;padding:6px 14px;border-radius:4px;cursor:pointer;transition:all 0.15s;}#bp-locale-toggle:hover{border-color:var(--bp-cyan);background:rgba(0,212,255,0.06);}[dir="rtl"] #bp-locale-toggle{right:auto;left:16px;}#bp-share-btn{font-size:13px;padding:8px 14px;}@media(max-width:480px){#bp-share-btn{display:none;}}`;
  document.head.appendChild(s);
})();

document.addEventListener('DOMContentLoaded',async()=>{
  bpApplyLocale(BP_LOCALE);
  const actions=document.querySelector('.bp-summary-bar__actions');
  if(actions){
    const shareBtn=document.createElement('button');
    shareBtn.id='bp-share-btn';shareBtn.className='bp-btn bp-btn--ghost';
    shareBtn.textContent='\ud83d\udd17 '+bpT('shareBtn');
    shareBtn.onclick=bpShowSharePanel;
    actions.insertBefore(shareBtn,actions.firstChild);
  }
  const restored=await bpRestoreFromShareHash();
  if(restored&&typeof bpGoToStage==='function'){
    setTimeout(()=>{
      if(restored.hasComponents){
        bpGoToStage(3);
        if(typeof bpRenderCustomize==='function')bpRenderCustomize();
      }else if(restored.hasTier){
        if(window.BP?.useCase&&window.BP?.monitor_target){
          bpGoToStage(2);
          if(typeof bpRenderBlueprint==='function')bpRenderBlueprint();
        }
      }
    },300);
  }
});
